import { CheckCircleTwoTone, MinusCircleTwoTone } from '@ant-design/icons';
import { Badge, Col, Row, Tooltip, Typography } from 'antd';
import _ from 'lodash';
import { PropTypes } from 'mobx-react';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import AmberWarningLamp from 'assets/icons/amberWarningLamp.svg';
import MilLamp from 'assets/icons/milLamp.svg';
import ProtectLamp from 'assets/icons/protectLamp.svg';
import RedStopLamp from 'assets/icons/redStopLamp.svg';
import { calcMileage } from 'helpers/unitCalculations';
import { getPriorityTagColor } from 'services/Issue';
import { customFleets, prescriptiveMaintenanceMessages } from 'shared';
import { CurrentUserStore, ShopStore } from 'stores';

import AmberWarningLampOff from 'assets/icons/amberWarningLampOff.svg';
import MilLampOff from 'assets/icons/milLampOff.svg';
import ProtectLampOff from 'assets/icons/protectLampOff.svg';
import RedStopLampOff from 'assets/icons/redStopLampOff.svg';

import DeviceStore from 'stores/Classes/DeviceStore';

const { Text } = Typography;

const CarIndicator = styled.div`
  height: 100%;
  width: 8px;
  position: absolute;
  top: 0;
  padding: 0;
  background-color: ${(props) => props.color || 'initial'};
`;

const StyledDiv = styled.div`
  font-size: 12px;
  padding-top: 5px;
  text-decoration: underline dotted;
  font-weight: bold;
`;

const StyledBadge = ({ count, backgroundColor }) => (
  <Badge
    showZero={true}
    count={String(count)}
    style={{ backgroundColor }}
    overflowCount={1000000}
  />
);

const VehicleResolvedContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

StyledBadge.propTypes = {
  count: PropTypes.any,
  backgroundColor: PropTypes.string,
};

const CarIDSection = styled.div`
  p {
    margin-bottom: 0px;
  }
`;

const WrapperVehiculeIconMobile = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 10px;
  gap: 4px;
`;

const lampColours = [
  {
    key: 'amberWarningLamp',
    color: '#ff812a',
  },
  {
    key: 'malfunctionLamp',
    color: '#ff812a',
  },
  {
    key: 'protectWarningLamp',
    color: '#ffb37b',
  },
  {
    key: 'redStopLamp',
    color: '#ff0000',
  },
  {
    key: 'off',
    color: '#b4b4b4',
  },
];

export const renderLastTriggeredAtV2 = (car) => {
  const stringDate = new Date(car.last_triggered_at).toString();
  const outputString = _.get(stringDate.split('GMT'), 0, stringDate);

  return <p>{outputString}</p>;
};

export const renderIndicatorV2 = (car) => {
  if (parseInt(car.critical) > 0) {
    return <CarIndicator color={getPriorityTagColor(4)} />;
  }
  if (parseInt(car.major) > 0) {
    return <CarIndicator color={getPriorityTagColor(2)} />;
  }
  return <CarIndicator />;
};

export const renderVehicleResolvedV2 = (car) => {
  return (
    <VehicleResolvedContainer>
      {_.get(car, 'resolved') ? (
        <CheckCircleTwoTone style={{ fontSize: '200%' }} />
      ) : (
        <MinusCircleTwoTone style={{ fontSize: '200%' }} />
      )}
    </VehicleResolvedContainer>
  );
};

export const renderVehicleIdV2 = (car) => {
  return (
    <CarIDSection>
      <strong>{car.car_name}</strong>
      <p>
        {car.car_make} {car.car_model} {car.car_year}
      </p>
      <p>
        {`${Number(calcMileage(car.mileage_total).toFixed(1)).toReadable()}
        ${CurrentUserStore.user.displayedOdometer}`}
      </p>
      {!customFleets.wolfline.includes(ShopStore.currentShop.id) &&
        !customFleets.bmc.includes(ShopStore.currentShop.id) && (
          <p>{Math.round(car.engine_hours / 3600)} engine hours</p>
        )}
      <p>
        <Link to={`/car/${car.id_car}`}>View Details</Link>
      </p>
    </CarIDSection>
  );
};

export const renderEngineLightStatus = (car) => {
  const engineLightStatus = _.get(car, 'engine_light_status');
  const activationDate = _.get(car, 'active_from');
  const { device } = DeviceStore;
  const isTablet = device === 'tablet';
  const isMobile = device === 'mobile';

  const iconList = [
    // Commented out because it's not used in the app but we're getting it from the API
    // {
    //   key: 'generalVehicleWarningLight',
    //   icon: AmberWarningLamp,
    //   iconOff: AmberWarningLampOff,
    //   tooltip: 'Engine Light',
    // },
    {
      key: 'amberWarningLamp',
      icon: AmberWarningLamp,
      iconOff: AmberWarningLampOff,
      tooltip: isMobile ? 'Check Engine Lamp' : 'Check Engine',
    },
    {
      key: 'malfunctionLamp',
      icon: MilLamp,
      iconOff: MilLampOff,
      tooltip: isMobile ? 'Mil Lamp' : 'Malfunction',
    },
    {
      key: 'protectWarningLamp',
      icon: ProtectLamp,
      iconOff: ProtectLampOff,
      tooltip: isMobile ? 'Protect Lamp' : 'Protect',
    },
    {
      key: 'redStopLamp',
      icon: RedStopLamp,
      iconOff: RedStopLampOff,
      tooltip: isMobile ? 'Stop Lamp' : 'Red Stop',
    },
  ];

  const resultIcons = iconList.map((item) => {
    const isActive = _.get(engineLightStatus, item.key);
    const activeFrom = _.get(activationDate, item.key);
    const iconUrl = isActive ? item.icon : item.iconOff;

    const IconElement = (
      <img
        src={iconUrl}
        key={item.key}
        width={isMobile ? 16 : 35}
        height={isMobile ? 16 : 35}
      />
    );

    const formattedDate = activeFrom
      ? `On since ${moment(activeFrom).format(
          'MMM D, YYYY'
        )} (${moment().diff(moment(activeFrom), 'days')} Days)`
      : '';

    return (
      <Tooltip
        key={item.key}
        title={formattedDate}
        styles={{ body: { textAlign: 'center' } }}
      >
        {isMobile ? (
          <Row type="flex" align="middle" justify="space-between">
            <WrapperVehiculeIconMobile>
              <div>{IconElement}</div>
              <Text>{`${item.tooltip}:`}&nbsp;</Text>
            </WrapperVehiculeIconMobile>
            <Col>
              <Text type={!isActive ? 'danger' : ''}>
                {isActive ? ' On' : ' Off'}
              </Text>
            </Col>
          </Row>
        ) : (
          <>
            {isActive ? (
              <>
                {IconElement}
                <StyledDiv
                  style={{
                    color: lampColours.find((lamp) =>
                      isActive ? lamp.key === item.key : lamp.key === 'off'
                    )?.color,
                  }}
                >
                  {item.tooltip}
                </StyledDiv>
              </>
            ) : (
              <div>--</div>
            )}
          </>
        )}
      </Tooltip>
    );
  });

  if (_.isEmpty(resultIcons)) {
    return null;
  }

  if (isMobile) {
    return <>{resultIcons}</>;
  }
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '10px',
      }}
    >
      {resultIcons}
    </div>
  );
};

export const renderSuggestedActionV2 = (car) => {
  const maxPriority = car.critical > 0 ? '4' : '2';
  let now = new Date();
  //Temp maxMajorIssueDate to always trigger the stale major message
  let maxMajorIssueDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - 8
  );
  const issueCounts = null; // car.issueCounts;
  return (
    <p>
      {prescriptiveMaintenanceMessages(
        maxPriority,
        maxMajorIssueDate,
        false,
        issueCounts
      )}
    </p>
  );
};

export const renderIssuesV2 = (car) => {
  let criticalCount = car.critical || 0;
  let majorCount = car.major || 0;
  let minorCount = car.minor || 0;

  return (
    <div style={{ minWidth: '150px' }}>
      <Row gutter={[8, 8]}>
        <Col xl={8} xs={6}>
          <StyledBadge
            count={criticalCount}
            backgroundColor={getPriorityTagColor(4)}
          />
        </Col>

        <Col xl={16} xs={18}>
          Critical
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col xl={8} xs={6}>
          <StyledBadge
            count={majorCount}
            backgroundColor={getPriorityTagColor(2)}
          />
        </Col>

        <Col xl={16} xs={18}>
          Major
        </Col>
      </Row>

      <Row gutter={[8, 8]}>
        <Col xl={8} xs={6}>
          <StyledBadge
            count={minorCount}
            backgroundColor={getPriorityTagColor(1)}
          />
        </Col>

        <Col xl={16} xs={18}>
          Minor
        </Col>
      </Row>
    </div>
  );
};
